import React, { useState, useEffect } from 'react';
import Loading from '../../components/Loading';

interface SeasonSelectProps {
  onSelect: (season: number) => void;
  /** 표시할 시즌 목록. 기본값은 기존 동작 유지용 */
  seasons?: number[];
  /** 카드에 찍을 문구. 점령전 회차처럼 숫자가 길 때 쓴다 */
  formatLabel?: (season: number) => string;
}

const SeasonSelect: React.FC<SeasonSelectProps> = ({ onSelect, seasons: seasonsProp, formatLabel }) => {
  /**
   * 시즌 목록은 서버가 준다. 없으면 **아무 시즌도 그리지 않는다.**
   *
   * 예전에는 기본값 `[20, 19]` 를 그려놨다가 응답이 오면 바꿨다. 그래서 사이트에 처음
   * 들어가면 없는 시즌이 잠깐 떴다 사라졌다(20·19 → 22). 캐시가 아니라 이 기본값 때문이다.
   */
  const seasons = seasonsProp ?? [];
  const latestSeason = seasons.length ? Math.max(...seasons) : 0;

  if (seasons.length === 0) {
    return (
      <Loading label="시즌을 불러오는 중…" padding="clamp(28px, 6vw, 48px)" />
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes pulse-glow {
            0%, 100% {
              box-shadow: 0 10px 40px rgba(102, 126, 234, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04), 0 0 0 0 rgba(102, 126, 234, 0);
            }
            50% {
              box-shadow: 0 10px 40px rgba(102, 126, 234, 0.2), 0 2px 8px rgba(0, 0, 0, 0.04), 0 0 20px 4px rgba(102, 126, 234, 0.3);
            }
          }

          @keyframes badge-pulse {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.05);
            }
          }
        `}
      </style>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 'clamp(12px, 3vw, 20px)',
        width: '100%'
      }}>
        {seasons.map(season => {
          const isLatest = season === latestSeason;
          return (
            <div
              key={season}
              onClick={() => onSelect(season)}
              style={{
                background: 'rgba(255, 255, 255, 0.6)',
                borderRadius: 'clamp(12px, 2.5vw, 16px)',
                padding: 'clamp(20px, 4vw, 28px) clamp(18px, 3vw, 24px)',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 10px 40px rgba(102, 126, 234, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 'clamp(110px, 22vw, 140px)',
                position: 'relative',
                overflow: 'visible',
                border: `2px solid ${isLatest ? 'rgba(102, 126, 234, 0.4)' : 'rgba(102, 126, 234, 0.15)'}`,
                backdropFilter: 'blur(10px)',
                animation: isLatest ? 'pulse-glow 2s ease-in-out infinite' : 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 20px 60px rgba(102, 126, 234, 0.15), 0 4px 16px rgba(0, 0, 0, 0.08)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                e.currentTarget.style.animation = 'none';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 10px 40px rgba(102, 126, 234, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.6)';
                e.currentTarget.style.animation = isLatest ? 'pulse-glow 2s ease-in-out infinite' : 'none';
              }}
            >
            {/* NEW 배지 */}
            {isLatest && (
              <div style={{
                position: 'absolute',
                top: 'clamp(-10px, -2vw, -12px)',
                right: 'clamp(10px, 2vw, 12px)',
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                color: 'white',
                padding: 'clamp(5px, 1vw, 6px) clamp(10px, 2vw, 12px)',
                borderRadius: 'clamp(12px, 2.5vw, 16px)',
                fontSize: 'clamp(10px, 2vw, 11px)',
                fontWeight: 800,
                boxShadow: '0 4px 12px rgba(255, 107, 107, 0.4)',
                letterSpacing: '1px',
                zIndex: 10,
                animation: 'badge-pulse 1.5s ease-in-out infinite'
              }}>
                NEW
              </div>
            )}


            {/* SEASON 텍스트 */}
            <div style={{
              fontSize: 'clamp(10px, 2vw, 12px)',
              fontWeight: 700,
              letterSpacing: '2px',
              color: '#667eea',
              marginBottom: 'clamp(5px, 1vw, 6px)',
              opacity: 0.7,
              position: 'relative',
              zIndex: 1
            }}>SEASON</div>

            {/* 시즌 번호 */}
            <div style={{
              // 라벨이 길면(점령전 회차 등) 카드를 넘치지 않게 줄인다
              fontSize: (formatLabel ? formatLabel(season) : String(season)).length > 4
                ? 'clamp(15px, 3vw, 22px)'
                : 'clamp(32px, 6vw, 44px)',
              fontWeight: 900,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              lineHeight: 1,
              position: 'relative',
              zIndex: 1
            }}>{formatLabel ? formatLabel(season) : season}</div>
          </div>
        );
        })}
      </div>
    </>
  );
};

export default SeasonSelect;
